import React from 'react';
import { Container, Typography, Box, Paper, Link } from '@mui/material';

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        
        <Typography variant="body1" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            This Privacy Policy describes how we collect, use, and handle your personal information when you use our service. We are committed to protecting your privacy and ensuring you have a positive experience on our platform.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We collect the following types of information:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                <strong>Account Information:</strong> When you create an account, we collect your first name, last name, email address, and password (stored securely using encryption).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Profile Information:</strong> Optional information such as your avatar URL and user preferences (theme, notifications, language).
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Address Information:</strong> Shipping addresses and recipient addresses for your postcards, including name, street address, city, state, country, and postal code.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Order Information:</strong> Details about your orders, including payment information, order status, and delivery information.
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>Usage Data:</strong> Information about how you use our service, including your interactions with our platform.
              </Typography>
            </li>
          </ul>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use your information to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                Process and fulfill your orders
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Provide customer support and respond to your inquiries
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Send you important updates about your orders and account
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Improve our services and user experience
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Comply with legal obligations
              </Typography>
            </li>
          </ul>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate security measures to protect your personal information. Your password is encrypted using industry-standard methods, and we use secure protocols for data transmission.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Third-Party Services
          </Typography>
          <Typography variant="body1" paragraph>
            We use the following third-party services:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                <strong>Stripe:</strong> For secure payment processing. Their privacy policy can be found at{' '}
                <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                  https://stripe.com/privacy
                </Link>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>PrintOne:</strong> For postcard printing and delivery services. Their privacy policy can be found at{' '}
                <Link href="https://www.printone.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  https://www.printone.com/privacy-policy
                </Link>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>OpenAI:</strong> For AI-powered image generation. Their privacy policy can be found at{' '}
                <Link href="https://openai.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  https://openai.com/privacy-policy
                </Link>
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph>
            These services have their own privacy policies and data handling practices. We recommend reviewing their privacy policies to understand how they handle your data.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1">
                Access your personal information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Correct inaccurate information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Request deletion of your information
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                Opt-out of marketing communications
              </Typography>
            </li>
          </ul>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy or our data practices, please{' '}
            <Link href="/contact">
              contact us
            </Link>.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy; 